import React, { useEffect, useState } from 'react';
import { useNavigate } from '../lib/routerCompat';
import { useToast } from '../ToastContext';
import ProfileBuilder from '../components/ProfileBuilder';
import { db, HandleUnavailableError } from '../services/db';
import { ProfileFieldError, draftFields, draftFromRow } from '../lib/creatorProfile';
import type { ProfileBuilderStep } from '../lib/creatorProfile';
import type { CreatorProfileDraft, CreatorRow } from '../types';

/**
 * Editing an existing creator profile (PIT-64).
 *
 * Deliberately thin: it loads the row, hands it to the same {@link ProfileBuilder}
 * onboarding uses — in `edit` mode, which shows every field at once instead of
 * walking stages — and writes the result back. Nothing about a field's rules
 * lives here, so a validation change made for onboarding cannot fail to apply
 * to editing.
 *
 * ## Handle changes and links
 *
 * Profile deep links are `/creators/$id`, keyed on the numeric `creators.id`,
 * so changing a handle never breaks a link to the profile — the id is stable
 * for the row's lifetime and no route anywhere resolves a creator by handle.
 *
 * One thing a handle change does *not* update: `products.handle` is a copy
 * taken when a marketplace listing is created, and the affiliate link built
 * from it (`?aff=<handle>` in ProductsView) keeps carrying the old value.
 * Nothing breaks — the link keys on the product id — but the attribution tag
 * goes stale. Reconciling that belongs with whatever owns affiliate
 * attribution, not with this form.
 *
 * Writes go through `db.updateMyCreator`, whose `user_id` scope matches the RLS
 * update policy from PIT-45: a creator can only ever edit their own row.
 */
export default function ProfileEditView(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const [existing, setExisting] = useState<CreatorRow | null>(null);
  const [resolved, setResolved] = useState<boolean>(false);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

  // Demo mode resolves to the bundled showcase profile: there is no session to
  // own a row, and editing the profile the demo dashboard renders is the
  // coherent thing to show. Nothing is written back.
  useEffect(() => {
    let cancelled = false;
    db.getMyCreator()
      .then((row) => {
        if (cancelled) return;
        setExisting(row);
        // Nothing to edit yet — onboarding is where a profile gets made, and
        // its own guard will send them straight back if they do have one.
        if (!row) navigate({ to: '/onboarding' });
      })
      .catch(() => {
        // Treat a failed lookup as "nothing to edit" rather than showing an
        // empty form that would save over a row we never read.
        if (!cancelled) navigate({ to: '/creator' });
      })
      .finally(() => {
        if (!cancelled) setResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // One write for the whole form. Rethrows so the builder keeps the creator in
  // place with their edits intact when it fails.
  const save = async (
    draft: CreatorProfileDraft,
    steps: readonly ProfileBuilderStep[]
  ): Promise<void> => {
    if (db.demoMode) return;
    try {
      await db.updateMyCreator(draftFields(draft, steps));
      // Objects the row referenced before this save and no longer does: an
      // avatar that was replaced, a portfolio image that was removed. Deleted
      // only after the write lands, so a failed save never strands a creator
      // without the picture they still have on their profile. Best-effort by
      // design — a leaked object is cheaper than a broken profile.
      const kept = new Set<string>(
        [draft.avatarPath, ...draft.portfolioPaths].filter((p): p is string => Boolean(p))
      );
      const orphaned = [existing?.avatar_path, ...(existing?.portfolio_paths ?? [])]
        .filter((path): path is string => Boolean(path))
        .filter((path) => !kept.has(path));
      await db.removeCreatorMedia(orphaned);
    } catch (err) {
      if (err instanceof HandleUnavailableError) {
        throw new ProfileFieldError('handle', err.message);
      }
      showToast('Could not save your changes — please try again');
      throw new Error('save failed');
    }
  };

  const complete = (): void => {
    showToast(db.demoMode ? 'Demo mode — changes not saved' : 'Profile updated');
    navigate({ to: '/creator' });
  };

  // Signed URLs for media already on the row, so the form shows what is there
  // rather than an empty frame. One batched call, refreshed only when the row
  // changes.
  useEffect(() => {
    if (!existing) return undefined;
    let cancelled = false;
    db.getMediaUrls([existing.avatar_path, ...(existing.portfolio_paths ?? [])])
      .then((urls) => {
        if (!cancelled) setMediaUrls(urls);
      })
      .catch(() => {
        // Unsigned media renders as the emoji placeholder — no worse than
        // before the upload existed.
      });
    return () => {
      cancelled = true;
    };
  }, [existing]);

  if (!resolved || !existing) {
    return (
      <div className="view">
        <div className="panel">
          <div className="creator-name" style={{ fontSize: 27 }}>
            Loading your profile…
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProfileBuilder
      mode="edit"
      initialDraft={draftFromRow(existing)}
      onSave={save}
      onComplete={complete}
      onCancel={() => navigate({ to: '/creator' })}
      // No backend to ask in demo mode: the field simply stays quiet, and the
      // upload inputs disable themselves so the emoji placeholders stand in.
      onCheckHandle={db.demoMode ? undefined : (handle) => db.isHandleAvailable(handle)}
      onUploadImage={db.demoMode ? undefined : (file, kind) => db.uploadCreatorMedia(file, kind)}
      mediaUrls={mediaUrls}
      demoMode={db.demoMode}
    />
  );
}
